import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAttributionParams, trackEvent } from "../lib/analytics";
import {
  buildGoModernBookingUrl,
  getGoModernUrls,
  getSourcePageFromLocation,
} from "../utils/gomodern";

export const Section = ({ id, eyebrow, title, description, children, tone = "dark", className = "" }) => (
  <section id={id} className={`marketing-section marketing-section-${tone} ${className}`}>
    <div className="marketing-shell">
      {(eyebrow || title || description) && (
        <div className="marketing-section-header">
          {eyebrow && <p className="marketing-eyebrow">{eyebrow}</p>}
          {title && <h2>{title}</h2>}
          {description && <p className="marketing-section-description">{description}</p>}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const CTAButton = ({ children, onClick, variant = "primary", type = "button" }) => (
  <button
    type={type}
    className={`marketing-button ${variant === "secondary" ? "marketing-button-secondary" : "marketing-button-primary"}`}
    onClick={onClick}
  >
    {children}
  </button>
);

export const BookingCTA = ({
  label = "Book a 20-Minute Demo",
  variant = "primary",
  eventName = "book_demo_click",
  sourcePage,
  ctaClicked,
  requestedOffer,
  buyerType,
  urlType = "demo",
  className = "",
  onClick,
}) => {
  const location = useLocation();
  const urlMap = getGoModernUrls();
  const resolvedSourcePage = sourcePage || getSourcePageFromLocation(location);
  const href = useMemo(
    () =>
      buildGoModernBookingUrl({
        baseUrl: urlMap[urlType] || urlMap.demo,
        sourcePage: resolvedSourcePage,
        ctaClicked,
        requestedOffer,
        buyerType,
      }),
    [buyerType, ctaClicked, requestedOffer, resolvedSourcePage, urlMap, urlType]
  );

  function handleClick(event) {
    const attribution = getAttributionParams();
    const eventPayload = {
      source_page: resolvedSourcePage,
      cta_clicked: ctaClicked,
      requested_offer: requestedOffer,
      buyer_type: buyerType,
      destination_url: href,
      ...attribution,
    };

    trackEvent(eventName, eventPayload);
    trackEvent("gomodern_form_opened", eventPayload);

    if (typeof onClick === "function") {
      onClick(event);
    }
  }

  return (
    <a
      className={`marketing-button ${variant === "secondary" ? "marketing-button-secondary" : "marketing-button-primary"} ${className}`.trim()}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
    >
      {label}
    </a>
  );
};

export const FeatureCard = ({ title, description }) => (
  <article className="marketing-card">
    <h3>{title}</h3>
    <p>{description}</p>
  </article>
);

export const PrincipleCard = ({ title, description }) => (
  <article className="marketing-card marketing-principle-card">
    <h3>{title}</h3>
    <p>{description}</p>
  </article>
);

export const WorkflowStep = ({ title, items }) => (
  <article className="workflow-card">
    <div className="workflow-badge">{title.charAt(0)}</div>
    <h3>{title}</h3>
    <ul className="marketing-bullet-list compact">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </article>
);

export const PersonaSection = ({
  eyebrow,
  title,
  description,
  bullets,
  ctaLabel,
  onCtaClick,
  bookingProps,
}) => (
  <article className="persona-panel">
    <div>
      <p className="marketing-eyebrow">{eyebrow}</p>
      <h3>{title}</h3>
      <p className="marketing-body-copy">{description}</p>
      <ul className="marketing-bullet-list">
        {bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    </div>
    <div className="persona-panel-cta">
      {bookingProps ? (
        <BookingCTA label={ctaLabel} {...bookingProps} />
      ) : (
        <CTAButton onClick={onCtaClick}>{ctaLabel}</CTAButton>
      )}
    </div>
  </article>
);

export const ProductMockupPanel = () => (
  <aside className="product-mockup" aria-label="Example signal to opportunity review">
    <div className="product-mockup-header">
      <div>
        <p className="marketing-kicker">Example product view</p>
        <h3>Signal-to-Opportunity Review</h3>
      </div>
      <span className="example-pill">Example</span>
    </div>
    <div className="product-signal-grid">
      <div className="signal-card">
        <span>ICP fit</span>
        <strong className="signal-positive">Strong</strong>
      </div>
      <div className="signal-card">
        <span>Buying signal</span>
        <strong className="signal-positive">Active</strong>
      </div>
      <div className="signal-card">
        <span>Forecast risk</span>
        <strong className="signal-warning">Medium</strong>
      </div>
      <div className="signal-card">
        <span>Champion strength</span>
        <strong className="signal-warning">Weak</strong>
      </div>
      <div className="signal-card">
        <span>Economic buyer</span>
        <strong>Not confirmed</strong>
      </div>
    </div>
    <div className="product-detail-grid">
      <div className="detail-card">
        <p>MEDDICC gaps</p>
        <ul className="marketing-bullet-list compact">
          <li>Metrics</li>
          <li>Decision process</li>
          <li>Paper process</li>
        </ul>
      </div>
      <div className="detail-card">
        <p>Recommended next action</p>
        <strong>Secure executive discovery and validate decision criteria.</strong>
      </div>
    </div>
  </aside>
);

export const DemoForm = () => {
  return (
    <div className="demo-form demo-booking-card">
      <p className="marketing-kicker">Book a walkthrough</p>
      <h3>Choose the right walkthrough for your revenue motion</h3>
      <p className="marketing-body-copy">
        We&apos;ll tailor the conversation around account prioritisation, signal workflows, MEDDICC
        discipline, pipeline quality, founder-led sales, sales engineering alignment, or GTM
        engineering services.
      </p>
      <ul className="marketing-bullet-list compact">
        <li>Submit your demo request</li>
        <li>We review your GTM context</li>
        <li>We follow up with suitable timeslots</li>
      </ul>
      <BookingCTA
        label="Book a 20-Minute Demo"
        eventName="book_demo_click"
        sourcePage="home"
        ctaClicked="final_book_20_min_demo"
        requestedOffer="BestSalesCoach.ai Platform Demo"
        buyerType="General"
        urlType="demo"
      />
      <p className="demo-form-note">
        When you submit the demo form, you agree to our{" "}
        <Link className="marketing-text-link" to="/terms">
          Terms and Conditions
        </Link>{" "}
        and{" "}
        <Link className="marketing-text-link" to="/privacy">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
};
